import React, { useEffect, useState } from "react";
import { catchError, handleInventoryResponseAlerts, renderInvalidFormErrors } from "../../utils/AlertUtils";
import FormComponent from "../generic/FormComponent";
import MaterialTypeDropdown from "../shared/MaterialTypeDropdown";
import SizeDropdown from "../shared/SizeDropdown";
import QuantityUnitDropdown from "../shared/QuantityUnitDropdown";
import SharedAlertBanner from "../shared/SharedAlertBanner";
import { createInitialInventory } from "../../services/MaterialAPI";
import { MaterialInventoryItem } from "../../types/InventoryItem";
import AddAnotherItemButton from "../generic/AddAnotherItemButton";
import SubmitButton from "../generic/SubmitButton";
import CreateMaterialTypeForm from "../materials/CreateMaterialTypeForm";
import CreateMaterialSizeForm from "../materials/CreateMaterialSizeForm";
import ButtonGroupOrganizer from "../generic/ButtonGroupOrganizer";
import InputField from "../generic/InputField";
import RowItem from "../generic/RowItem";  // Importing RowItem

const BUTTON_LABELS = {
    ADD_MATERIAL_TYPE: "Add Material Type",
    ADD_MATERIAL_SIZE: "Add Material Size"
};

const AddMaterialForm: React.FC<{}> = ({}) => {
    const defaultItem = {
        material_type_id: null,
        color_code: "",
        color_name: "",
        material_description: "",
        material_size_id: null,
        quantity_unit: "",
        quantity: 0,
        average_price_per_unit: 0,
    };

    const [items, setItems] = useState<MaterialInventoryItem[]>([defaultItem]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState<string | null>(null);
    const [successAlert, setSuccessAlert] = useState<{ message: string; title?: string } | null>(null);
    const [errorAlert, setErrorAlert] = useState<{ message: string; title?: string } | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (items.length === 0) {
            addNewItem();
        }
    }, [items]);

    const handleItemChange = <K extends keyof MaterialInventoryItem>(
        index: number,
        field: K,
        value: MaterialInventoryItem[K],
    ) => {
        setItems((prevItems) => {
            const updatedItems = [...prevItems];
            updatedItems[index][field] = value;
            return updatedItems;
        });
        clearAlerts();
    };

    const clearAlerts = () => {
        setSuccessAlert(null);
        setErrorAlert(null);
    };

    const addNewItem = () => {
        setItems((prevItems) => [...prevItems, { ...defaultItem }]);
    };

    const removeItem = (index: number) => {
        setItems((prevItems) => prevItems.filter((_, i) => i !== index));
    };

    const openModal = (content: string) => {
        setModalContent(content);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalContent(null);
        setRefreshKey((prevKey) => prevKey + 1); // Trigger refresh
    };

    const isFormValid = (items: any[]): boolean => {
        return items.every((item) => {
            const requiredFields = [
                item.material_type_id,
                item.material_size_id,
                item.quantity_unit,
                item.color_name,
            ];

            const hasValidRequiredFields = requiredFields.every(Boolean);
            const isValidQuantity = !isNaN(item.quantity) && item.quantity >= 0;
            const isValidPrice = !isNaN(item.average_price_per_unit) && item.average_price_per_unit >= 0;

            return hasValidRequiredFields && isValidQuantity && isValidPrice;
        });
    };

    const handleSubmit = async () => {
        renderInvalidFormErrors(items, isFormValid, setErrorAlert);
        setIsLoading(true);

        try {
            const validItems = items.map(({
                                              material_type_id,
                                              material_size_id,
                                              color_code,
                                              color_name,
                                              material_description,
                                              quantity_unit,
                                              quantity,
                                              average_price_per_unit,
                                          }) => ({
                material_type_id,
                material_size_id,
                color_code,
                color_name: color_name.trim(),
                material_description: material_description.trim(),
                quantity_unit: quantity_unit.trim(),
                quantity,
                average_price_per_unit,
            }));

            const response = await createInitialInventory(validItems);

            handleInventoryResponseAlerts(response, setSuccessAlert, setErrorAlert);

            if (response.errors && response.errors.length > 0) {
                const invalidItems = response.errors.map((error: any) => error?.record);
                setItems(invalidItems);
            } else {
                setItems([{ ...defaultItem }]);
                setErrorAlert(null);
            }
        } catch (error: any) {
            catchError(error, setErrorAlert);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={(e) => e.preventDefault()}>
            <FormComponent title={"Add Materials"} isFullScreen={true}>
                <SharedAlertBanner
                    successAlert={successAlert}
                    errorAlert={errorAlert}
                    onCloseSuccess={() => setSuccessAlert(null)}
                    onCloseError={() => setErrorAlert(null)}
                />

                {isModalOpen && (
                    <div>
                        {modalContent === "materialType" && (
                            <CreateMaterialTypeForm onCloseModal={closeModal} />
                        )}
                        {modalContent === "materialSize" && (
                            <CreateMaterialSizeForm onCloseModal={closeModal} />
                        )}
                    </div>
                )}

                <div className="flex flex-row pb-8 space-x-4">
                    <button className={"primary"} onClick={() => openModal("materialType")}>
                        {BUTTON_LABELS.ADD_MATERIAL_TYPE}
                    </button>
                    <button className={"primary"} onClick={() => openModal("materialSize")}>
                        {BUTTON_LABELS.ADD_MATERIAL_SIZE}
                    </button>
                </div>

                {items.map((item, index) => (
                    <RowItem
                        key={index}
                        index={index}
                        showRemoveButton={items.length > 1}
                        onRemove={removeItem}
                    >
                        <MaterialTypeDropdown
                            value={item.material_type_id}
                            onChange={(value) => handleItemChange(index, "material_type_id", value)}
                            refreshKey={refreshKey}
                        />
                        <SizeDropdown
                            selectedSize={item.material_size_id}
                            onSelectSize={(value) => handleItemChange(index, "material_size_id", value)}
                            refreshKey={refreshKey}
                        />
                        {item.material_type_id && item.material_size_id && (
                            <>
                                <InputField
                                    type="text"
                                    value={item.color_name}
                                    onChange={(value) => handleItemChange(index, "color_name", value as string)}
                                    label="Color Name"
                                    placeholder="Enter color name"
                                    required
                                />
                                <InputField
                                    type="text"
                                    value={item.color_code}
                                    onChange={(value) => handleItemChange(index, "color_code", value as string)}
                                    label="Color Code"
                                    placeholder="Enter color code"
                                    tooltip="If you don't provide a color code, we will automatically assign a unique, normalized color code for you."
                                />
                                <InputField
                                    type="text"
                                    value={item.material_description}
                                    onChange={(value) =>
                                        handleItemChange(index, "material_description", value as string)
                                    }
                                    label="Material Description"
                                    placeholder="Enter material description"
                                    tooltip="Provide any additional details about the material, such as unique features, usage guidelines, or specifications."
                                />
                                <QuantityUnitDropdown
                                    value={item.quantity_unit}
                                    onChange={(value) =>
                                        handleItemChange(index, "quantity_unit", value?.toString() || "")
                                    }
                                />
                                <InputField
                                    type="number"
                                    step="0.01"
                                    value={String(item.quantity)}
                                    onChange={(value) =>
                                        handleItemChange(index, "quantity", parseFloat(value.toString()) || 0)
                                    }
                                    label="Quantity"
                                    placeholder="Enter quantity"
                                />
                                <InputField
                                    type="number"
                                    step="0.01"
                                    value={String(item.average_price_per_unit) || ""}
                                    onChange={(value) =>
                                        handleItemChange(index, "average_price_per_unit", value.toString())
                                    }
                                    label="Unit Price"
                                    placeholder="Enter unit price"
                                />
                            </>
                        )}
                    </RowItem>
                ))}

                <ButtonGroupOrganizer>
                    <AddAnotherItemButton onAdd={addNewItem} />
                    <SubmitButton isLoading={isLoading} disabled={!isFormValid(items)} onClick={handleSubmit} />
                </ButtonGroupOrganizer>
            </FormComponent>
        </form>
    );
};

export default AddMaterialForm;
